import { inject, Injectable, signal } from '@angular/core';
import { TenantService } from './tenant.service';
import { NavigationEnd, Router } from '@angular/router';
import { Tenant } from '../models/tenant.model';
import { GetTenantRequest } from '../models/get-tenant-request';
import { ToastService } from '../../../core/services/toast.service';
import { DataStoreService } from '../tabs/data-lakes-tab/services/data-store.service';
import { WorkspaceService } from '../tabs/workspaces-tab/services/workspace.service';
import { CatalogService } from '../tabs/catalogs-tab/services/catalog.service';

@Injectable({
  providedIn: 'root'
})
export class TenantContextService {
  private tenantService = inject(TenantService);

  private router = inject(Router);
  private toastService = inject(ToastService);
  private dataStoreService = inject(DataStoreService);
  private workspaceService = inject(WorkspaceService);
  private catalogService = inject(CatalogService);

  tenantList = signal<Tenant[]>([]);
  tenant = signal<Tenant | null>(null);
  isLoading = signal<boolean>(false);
  hasTenantError = signal<boolean>(false);

  constructor() {
    // on load
    this.loadTenantList();

    // subscription to all changes to the route
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (this.isLoading()) return;
        this.handleRoute();
      }
    });
  }

  private getCurrentTenantIdFromRoute(): string | null {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('tenantId');
  }

  private handleRoute(): void {
    // wait until tenant list is loaded
    if (this.isLoading() || this.tenantList().length === 0) return;

    const routeTenantId = this.getCurrentTenantIdFromRoute();
    const url = this.router.url;
    if (!url.startsWith('/tenant-management')) return;

    if (routeTenantId) {
      // navigation to /tenant-management/:tenantId
      const isValid: boolean = this.tenantList().some(tenant => tenant.tenantId === routeTenantId);
      const isActive: boolean = this.tenant()?.tenantId === routeTenantId;
      if (isValid && isActive) {
        return;
      } else if (isValid && !isActive) {
        this.loadTenant(routeTenantId);
      } else {
        // fallback to first tenant
        this.router.navigate(['/tenant-management', this.tenantList()[0].tenantId]);
      }
    } else {
      // navigation to /tenant-management with no tenantId
      if (this.tenant()) {
        this.router.navigate(['/tenant-management', this.tenant()?.tenantId]);
      } else {
        this.router.navigate(['/tenant-management', this.tenantList()[0].tenantId]);
      }
    }
  }

  setContextTenant(tenant: Tenant): void {
    this.tenant.set(tenant);
    this.dataStoreService.getDataLakeList({ tenantId: tenant.tenantId });
    this.dataStoreService.getLandingZoneList({ tenantId: tenant.tenantId });
    this.workspaceService.getWorkspaceList({ tenantId: tenant.tenantId });
    this.catalogService.getCatalogList({ tenantId: tenant.tenantId });
  }
  
  // load tenant list and filter out empty tenant names and sort alphabetically by alias grouped by name
  loadTenantList(): void {   
    this.isLoading.set(true);
    
    this.tenantService.listTenants().subscribe({
      next: (tenants) => {
        if (tenants !== null) {
          const filtered = tenants
            .filter(tenant => tenant.tenantName)
            .sort((a, b) => this.compareTenants(a, b));
          this.tenantList.set(filtered)
        }  
        this.isLoading.set(false);
        this.handleRoute();
      },
      error: (error: string) => {
        if (error === "403: Forbidden") {  
          this.toastService.showError("You don't have permission to access the tenant management utility.");
        } else {
          this.toastService.showError("Internal server error. Please try again later.");
        }
        this.isLoading.set(false);
        this.handleRoute();
      }
    });
  }

  loadTenant(tenantId: string): void {
    this.isLoading.set(true);
    this.hasTenantError.set(false);

    const request: GetTenantRequest = { tenantId };
    this.tenantService.getTenant(request).subscribe({
      next: (tenant) => {
        if (tenant) {
          this.setContextTenant(tenant);
          this.isLoading.set(false);
          this.hasTenantError.set(false);
          this.handleRoute();
        } else {
          this.isLoading.set(false);
          this.hasTenantError.set(true);
          this.toastService.showError("Tenant not found.");
        }
      },
      error: (error: string) => {
        if (error === "403: Forbidden") {  
          this.toastService.showError("You don't have permission to access the tenant management utility.");
        } else {
          this.toastService.showError("Internal server error. Please try again later.");
        }
        this.isLoading.set(false);
        this.hasTenantError.set(true);
      }
    });
  }

  private compareTenants(a: Tenant, b: Tenant): number {
    return a.tenantName.localeCompare(b.tenantName) ||
      (a.tenantAlias ?? '').localeCompare(b.tenantAlias ?? '');
  }

  applyTenantChanges(updatedTenant: Tenant): void {
    const currentTenant = this.tenant();
    if (currentTenant && currentTenant.tenantId === updatedTenant.tenantId) {
      this.tenant.set(updatedTenant);
    }

    this.tenantList.update(list => {
      if (list.length === 0) return list;
      const updatedList = list.map(tenant =>
        tenant.tenantId === updatedTenant.tenantId ? updatedTenant : tenant
      );
      return [...updatedList].sort((a, b) => this.compareTenants(a, b));
    });
  }
}